import type {
  Citation,
  ClaimEvidenceMap,
  GroundedAnswerMode,
  GroundedClaimInput,
  GroundedCompareInput,
  GroundedTopicInput,
  GroundingBudget,
} from "@gencow/core";
import { clampConfidence, dedupeCitations, extractSignalTerms } from "./server-grounding-common.js";

const NEGATION_TERMS = new Set(["not", "no", "never", "cannot", "cant", "can't", "wont", "won't", "without"]);
const POLARITY_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "been",
  "being",
  "by",
  "for",
  "from",
  "in",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "this",
  "to",
  "was",
  "were",
  "with",
  "within",
]);

export function verifyClaims(params: {
  claims: GroundedClaimInput[];
  citations: Citation[];
  mode: GroundedAnswerMode;
  compare?: GroundedCompareInput;
  topic?: GroundedTopicInput;
  budget: GroundingBudget;
}): ClaimEvidenceMap[] {
  return params.claims.map((claim, index) => {
    const rankedAll = rankCitationsForClaim(claim, params.citations);
    const hasContradiction = params.citations.some((citation) =>
      hasContradictionRisk(claim.claimText, citation.snippet),
    );
    const ranked = rankedAll
      .filter((citation) => citation.coverage !== "context")
      .slice(0, params.budget.maxCitationsPerClaim);
    const base = {
      claimId: claim.claimId ?? `claim-${index + 1}`,
      claimText: claim.claimText,
      citations: ranked,
    };

    if (params.mode === "compare" && params.compare) {
      return {
        ...base,
        ...withContradictionReason(verifyCompareClaim(params.compare, ranked), hasContradiction),
      };
    }

    if (params.mode === "topic") {
      return {
        ...base,
        ...withContradictionReason(verifyTopicClaim(params.topic, ranked), hasContradiction),
      };
    }

    return { ...base, ...withContradictionReason(verifyGenericClaim(ranked), hasContradiction) };
  });
}

function rankCitationsForClaim(claim: GroundedClaimInput, citations: Citation[]): Citation[] {
  const terms = [
    ...extractSignalTerms(claim.claimText),
    ...(claim.requiredTerms ?? []).map((term) => term.toLowerCase()),
  ];
  if (terms.length === 0) return citations;

  return dedupeCitations(citations)
    .map((citation) => {
      const snippet = citation.snippet.toLowerCase();
      const matched = terms.filter((term) => snippet.includes(term)).length;
      const ratio = matched / terms.length;
      const contradictionRisk = hasContradictionRisk(claim.claimText, citation.snippet);
      const coverage = contradictionRisk
        ? "context"
        : matched >= 2 || ratio >= 0.5
          ? "direct"
          : matched > 0
            ? "partial"
            : "context";
      return {
        ...citation,
        coverage,
        confidence: contradictionRisk ? 0 : clampConfidence(Math.max(ratio, matched > 0 ? 0.35 : 0)),
      } satisfies Citation;
    })
    .sort((left, right) => right.confidence - left.confidence);
}

function withContradictionReason<T extends Pick<ClaimEvidenceMap, "status" | "missingEvidenceReasons">>(
  result: T,
  hasContradiction: boolean,
): T {
  if (!hasContradiction || result.status === "supported") return result;
  return {
    ...result,
    missingEvidenceReasons: [
      ...(result.missingEvidenceReasons ?? []),
      "Potential contradiction or negation mismatch found in retrieved evidence",
    ],
  };
}

function verifyGenericClaim(
  citations: Citation[],
): Pick<ClaimEvidenceMap, "status" | "missingEvidenceReasons"> {
  if (citations.some((citation) => citation.coverage === "direct")) {
    return { status: "supported" };
  }
  if (citations.length > 0) {
    return {
      status: "partially_supported",
      missingEvidenceReasons: ["Only partial citation coverage is available for this claim"],
    };
  }
  return {
    status: "insufficient_evidence",
    missingEvidenceReasons: ["No direct citation found for this claim"],
  };
}

function verifyCompareClaim(
  compare: GroundedCompareInput,
  citations: Citation[],
): Pick<ClaimEvidenceMap, "status" | "missingEvidenceReasons"> {
  const leftCovered = sideCovered(compare.left, citations);
  const rightCovered = sideCovered(compare.right, citations);
  if (leftCovered && rightCovered && citations.some((citation) => citation.coverage === "direct")) {
    return { status: "supported" };
  }

  const missing = [
    ...(!leftCovered ? [`Missing compare-side evidence for ${compare.left}`] : []),
    ...(!rightCovered ? [`Missing compare-side evidence for ${compare.right}`] : []),
  ];

  return {
    status: citations.length > 0 ? "partially_supported" : "insufficient_evidence",
    missingEvidenceReasons:
      missing.length > 0 ? missing : ["Compare evidence is not balanced across both sides"],
  };
}

function verifyTopicClaim(
  topic: GroundedTopicInput | undefined,
  citations: Citation[],
): Pick<ClaimEvidenceMap, "status" | "missingEvidenceReasons"> {
  const minDistinctSections = topic?.minDistinctSections ?? 2;
  const distinctSections = new Set(citations.map((citation) => citation.sectionId ?? citation.chunkIds[0]))
    .size;

  if (distinctSections >= minDistinctSections) {
    return { status: "supported" };
  }
  if (citations.length > 0) {
    return {
      status: "partially_supported",
      missingEvidenceReasons: [`Topic section coverage is ${distinctSections}/${minDistinctSections}`],
    };
  }
  return {
    status: "insufficient_evidence",
    missingEvidenceReasons: ["No topic evidence found"],
  };
}

function sideCovered(side: string, citations: Citation[]): boolean {
  const terms = extractSignalTerms(side);
  if (terms.length === 0) return false;
  return citations.some((citation) => {
    const snippet = citation.snippet.toLowerCase();
    return terms.some((term) => snippet.includes(term));
  });
}

function hasContradictionRisk(claimText: string, snippet: string): boolean {
  const claimTokens = tokenizeForPolarity(claimText);
  const snippetTokens = tokenizeForPolarity(snippet);
  const sharedTerms = meaningfulSharedTerms(claimTokens, snippetTokens);
  if (sharedTerms.length < 2) return false;

  return sharedTerms.some((term) => {
    const claimNegated = isTermNegated(claimTokens, term);
    const snippetNegated = isTermNegated(snippetTokens, term);
    return claimNegated !== snippetNegated && (claimNegated || snippetNegated);
  });
}

function meaningfulSharedTerms(leftTokens: string[], rightTokens: string[]): string[] {
  const right = new Set(rightTokens);
  return [
    ...new Set(
      leftTokens.filter(
        (token) =>
          token.length > 1 &&
          right.has(token) &&
          !NEGATION_TERMS.has(token) &&
          !POLARITY_STOP_WORDS.has(token),
      ),
    ),
  ];
}

function isTermNegated(tokens: string[], term: string): boolean {
  return tokens.some((token, index) => {
    if (token !== term) return false;
    const windowStart = Math.max(0, index - 3);
    return tokens.slice(windowStart, index).some((candidate) => NEGATION_TERMS.has(candidate));
  });
}

function tokenizeForPolarity(text: string): string[] {
  return text.toLowerCase().match(/[\p{L}\p{N}']+/gu) ?? [];
}
